import { memo, useCallback, useEffect, useState } from "react";

import type { IconName } from "@react/components/Icon";

import { DropdownView } from "./Dropdown.view";

export type DropdownOption = {
  iconName?: IconName;
  name: string;
  value: string | Record<string, unknown>;
};

type Props = {
  align?: "left" | "right";
  ariaLabel?: string;
  errorMessage?: string;
  fluid?: boolean;
  hasError?: boolean;
  helperText?: string;
  iconName?: IconName;
  initialSelectedOption?: DropdownOption;
  isBorderless?: boolean;
  isDisabled?: boolean;
  label?: string;
  labelPosition?: "left" | "top";
  onSelectOption?: (option: DropdownOption) => void;
  options: DropdownOption[];
};

const DropdownBase = ({
  align,
  ariaLabel,
  errorMessage,
  fluid,
  hasError,
  helperText,
  iconName,
  initialSelectedOption,
  isBorderless,
  isDisabled,
  label,
  labelPosition,
  onSelectOption,
  options,
}: Props) => {
  const [selectedOption, setSelectedOption] = useState(
    () => initialSelectedOption ?? options[0]
  );

  const handleSelectOption = useCallback(
    (option: DropdownOption) => {
      setSelectedOption(option);
      onSelectOption?.(option);
    },
    [onSelectOption]
  );

  useEffect(() => {
    if (initialSelectedOption) {
      setSelectedOption(initialSelectedOption);
    }
  }, [initialSelectedOption]);

  return (
    <DropdownView
      align={align}
      ariaLabel={ariaLabel}
      errorMessage={errorMessage}
      fluid={fluid}
      hasError={hasError}
      helperText={helperText}
      iconName={iconName}
      isBorderless={isBorderless}
      isDisabled={isDisabled}
      label={label}
      labelPosition={labelPosition}
      onSelectOption={handleSelectOption}
      options={options}
      selectedOption={selectedOption}
    />
  );
};

export const Dropdown = memo(DropdownBase);
